interface RecompensasChartProps {
  data: {
    mes: string;
    pix_valor: number;
    pagamento_conta_valor: number;
    voucher_valor: number;
    curso_valor: number;
    experiencia_valor: number;
    produto_valor: number;
  }[];
}

const COLORS: Record<string, string> = {
  pix_valor: '#059669',
  pagamento_conta_valor: '#2563eb',
  voucher_valor: '#d97706',
  curso_valor: '#7c3aed',
  experiencia_valor: '#e11d48',
  produto_valor: '#0891b2',
};

const LABELS: Record<string, string> = {
  pix_valor: 'PIX',
  pagamento_conta_valor: 'Pagamento de Contas',
  voucher_valor: 'Voucher',
  curso_valor: 'Cursos',
  experiencia_valor: 'Experiências',
  produto_valor: 'Produtos',
};

export default function RecompensasChart({ data }: RecompensasChartProps) {
  const width = 800;
  const height = 280;
  const padding = { top: 20, right: 20, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const tipos = Object.keys(COLORS) as (keyof typeof COLORS)[];

  // Max value for Y scale
  const maxY = Math.max(
    ...data.map((d) => tipos.reduce((sum, t) => sum + d[t as keyof typeof d] as number, 0)),
    1
  );

  const xScale = (i: number) => padding.left + (i / (data.length - 1)) * chartWidth;
  const yScale = (val: number) => padding.top + chartHeight - (val / maxY) * chartHeight;

  // Build stacked paths
  const buildAreaPath = (tipoIndex: number) => {
    const tipo = tipos[tipoIndex];
    let path = '';

    // Top line (cumulative up to this tipo)
    for (let i = 0; i < data.length; i++) {
      let cumSum = 0;
      for (let j = 0; j <= tipoIndex; j++) {
        cumSum += data[i][tipos[j] as keyof typeof data[0]] as number;
      }
      const x = xScale(i);
      const y = yScale(cumSum);
      path += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
    }

    // Bottom line (cumulative before this tipo, reversed)
    for (let i = data.length - 1; i >= 0; i--) {
      let cumSum = 0;
      for (let j = 0; j < tipoIndex; j++) {
        cumSum += data[i][tipos[j] as keyof typeof data[0]] as number;
      }
      const x = xScale(i);
      const y = yScale(cumSum);
      path += ` L ${x} ${y}`;
    }

    path += ' Z';
    return path;
  };

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct) => {
          const y = padding.top + chartHeight * (1 - pct);
          return (
            <g key={pct}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth={1}
                strokeDasharray="4,4"
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                className="text-[10px] fill-gray-400"
                style={{ fontSize: 10 }}
              >
                {(maxY * pct).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
              </text>
            </g>
          );
        })}

        {/* X axis labels */}
        {data.map((d, i) => (
          <text
            key={d.mes}
            x={xScale(i)}
            y={height - 10}
            textAnchor="middle"
            className="text-[10px] fill-gray-500"
            style={{ fontSize: 10 }}
          >
            {d.mes}
          </text>
        ))}

        {/* Stacked areas */}
        {tipos.map((_, idx) => (
          <path
            key={tipos[idx]}
            d={buildAreaPath(idx)}
            fill={COLORS[tipos[idx]]}
            opacity={0.7}
          />
        ))}

        {/* Top lines */}
        {tipos.map((_, idx) => {
          let path = '';
          for (let i = 0; i < data.length; i++) {
            let cumSum = 0;
            for (let j = 0; j <= idx; j++) {
              cumSum += data[i][tipos[j] as keyof typeof data[0]] as number;
            }
            const x = xScale(i);
            const y = yScale(cumSum);
            path += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
          }
          return (
            <path
              key={`line-${tipos[idx]}`}
              d={path}
              fill="none"
              stroke={COLORS[tipos[idx]]}
              strokeWidth={2}
            />
          );
        })}

        {/* Data points with tooltips */}
        {data.map((d, i) => {
          let cumSum = 0;
          return tipos.map((tipo, tIdx) => {
            const val = d[tipo as keyof typeof d] as number;
            cumSum += val;
            const prevCum = cumSum - val;
            const x = xScale(i);
            const yTop = yScale(cumSum);
            const yMid = yScale(prevCum + val / 2);
            return (
              <g key={`${i}-${tipo}`}>
                <circle cx={x} cy={yTop} r={3} fill={COLORS[tipo]} />
                <circle cx={x} cy={yMid} r={12} fill="transparent">
                  <title>{`${LABELS[tipo]} - ${d.mes}: ${val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`}</title>
                </circle>
              </g>
            );
          });
        })}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
        {tipos.map((tipo) => (
          <div key={tipo} className="flex items-center space-x-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[tipo] }} />
            <span className="text-[10px] text-gray-500">{LABELS[tipo]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}